/// <reference types="@types/google.maps" />

import { Search } from 'lucide-react'
import React, { Suspense, useEffect, useRef, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'
import { cn } from 'src/shared/utils/cn'

interface LocationAutoCompleteProps {
  onPlaceSelected: (place: PlaceResult) => void
  placeholder?: string
  types: string[]
  componentRestrictions?: google.maps.places.ComponentRestrictions
  refValue: 'street' | 'city' | 'full' | ''
  value?: string
  inputClassName?: string
  searchIconClassName?: string
  placeholderClassName?: string
  /** If true, the placeholder will be centered in the input. Otherwise, it will be left aligned. */
  centerPlaceholder?: boolean
}

export interface PlaceResult {
  street: string
  city: string
  state: string
  zip: string
  latitude: number
  longitude: number
}

export const LocationAutoCompleteContent: React.FC<LocationAutoCompleteProps> = ({
  onPlaceSelected,
  placeholder = 'Enter a location',
  types = ['address'],
  componentRestrictions = { country: 'us' },
  refValue,
  value,
  inputClassName,
  searchIconClassName,
  placeholderClassName,
  centerPlaceholder = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState(value || '')
  const [showPredictions, setShowPredictions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [inputFocused, setInputFocused] = useState(false)
  const { data: mapApiKey } = useMapApiKey()

  const shrinkPlaceholder = inputFocused || inputValue.length !== 0

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: mapApiKey,
    debounce: 500,
  })

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value || ''
      setInputValue(value || '')
    }
  }, [value])

  useEffect(() => {
    // Reset selected index when predictions change
    setSelectedIndex(-1)
  }, [placePredictions])

  const handlePlaceDetails = (placeId: string) => {
    if (!placesService) return

    placesService.getDetails(
      {
        placeId: placeId,
      },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (!placeDetails) return

        const addressComponents = placeDetails.address_components || []
        const geometry = placeDetails.geometry || {}

        const getComponent = (types: string[]) => {
          for (const type of types) {
            const component = addressComponents.find((component) => component.types.includes(type))
            if (component) {
              if (type === 'administrative_area_level_1' || type === 'administrative_area_level_2') {
                return component.short_name
              }
              return component.long_name
            }
          }
          return ''
        }

        const streetNumber = getComponent(['street_number'])
        const route = getComponent(['route'])
        const street = `${streetNumber} ${route}`.trim()
        const city = getComponent([
          'locality',
          'postal_town',
          'sublocality',
          'neighborhood',
          'administrative_area_level_2',
        ])
        const state = getComponent(['administrative_area_level_1', 'administrative_area_level_2'])
        const zip = getComponent(['postal_code'])

        const placeResult: PlaceResult = {
          street,
          city,
          state,
          zip,
          latitude: geometry.location?.lat() || 0,
          longitude: geometry.location?.lng() || 0,
        }

        onPlaceSelected(placeResult)

        if (inputRef.current) {
          let displayValue = ''
          switch (refValue) {
            case 'street':
              displayValue = street
              break
            case 'city':
              displayValue = `${city}, ${state}`
              break
            case 'full':
              displayValue = `${street}, ${city}, ${state} ${zip}`
              break
          }
          inputRef.current.value = displayValue
          setInputValue(displayValue)
        }
        setShowPredictions(false)
        setSelectedIndex(-1)
      },
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setShowPredictions(true)
    if (value.length > 1) {
      getPlacePredictions({
        input: value,
        types,
        componentRestrictions,
      })
    } else {
      setShowPredictions(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showPredictions || !placePredictions.length) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < placePredictions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          handlePlaceDetails(placePredictions[selectedIndex].place_id)
        }
        break
      case 'Escape':
        setShowPredictions(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowPredictions(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative">
      {centerPlaceholder === false && (
        <Search
          className={cn(
            'pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-primary',
            searchIconClassName,
          )}
        />
      )}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (inputValue.length > 2) {
            setShowPredictions(true)
          }
          setInputFocused(true)
        }}
        onBlur={() => setInputFocused(false)}
        className={cn(
          'peer flex h-14 w-full rounded-md border border-border bg-background py-2 pl-10 pr-3 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted focus-visible:border-2 focus-visible:border-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          inputClassName,
        )}
        placeholder=" "
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        role="combobox"
        aria-expanded={showPredictions}
        aria-controls="location-predictions"
        aria-autocomplete="list"
      />
      {centerPlaceholder === false ? (
        <label
          className={cn(
            'pointer-events-none absolute left-10 top-2 origin-[0] -translate-y-4 scale-75 bg-background px-1 text-sm text-muted transition-all duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75',
            placeholderClassName,
          )}
        >
          {placeholder}
        </label>
      ) : (
        <div className="pointer-events-none absolute left-1/2 top-2 origin-[50%] -translate-x-1/2 -translate-y-4 scale-75 bg-background px-1 text-sm text-muted transition-all duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75">
          <div
            className={cn(
              'flex-wrap-nowrap flex flex-shrink-0 flex-row items-center justify-center gap-0 bg-white transition-all duration-300',
              !shrinkPlaceholder && 'gap-[10px]',
            )}
          >
            <Search
              className={cn(
                'pointer-events-none max-w-0 flex-shrink-0 transition-all',
                !shrinkPlaceholder && 'max-w-full scale-100 opacity-100', // Show when placeholder is not shrunk
                searchIconClassName,
              )}
            />
            <label className={cn('text-nowrap', placeholderClassName)}>{placeholder}</label>
          </div>
        </div>
      )}
      {showPredictions && placePredictions.length > 0 && (
        <div
          ref={dropdownRef}
          id="location-predictions"
          role="listbox"
          className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-lg"
        >
          <ul className="py-1">
            {placePredictions.map((prediction, index) => (
              <li
                key={prediction.place_id}
                role="option"
                aria-selected={index === selectedIndex}
                className={`cursor-pointer px-4 py-2 ${
                  index === selectedIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                }`}
                onClick={() => handlePlaceDetails(prediction.place_id)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {prediction.description}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const LocationAutocompleteFormFieldSkeleton = () => {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-primary" />
      <input
        type="text"
        className="peer flex h-14 w-full rounded-md border border-border bg-background py-2 pl-10 pr-3 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted focus-visible:border-2 focus-visible:border-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        placeholder=" "
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        role="combobox"
        aria-expanded="false"
        aria-controls="location-predictions"
        aria-autocomplete="list"
      />
      <label className="pointer-events-none absolute left-10 top-2 origin-[0] -translate-y-4 scale-75 bg-background px-1 text-sm text-muted transition-all duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75">
        Enter a location
      </label>
    </div>
  )
}

export const LocationAutoComplete: React.FC<LocationAutoCompleteProps> = ({
  onPlaceSelected,
  placeholder = 'Enter a location',
  types = ['address'],
  componentRestrictions = { country: 'us' },
  refValue,
  value,
  inputClassName,
  searchIconClassName,
  placeholderClassName,
  centerPlaceholder = false,
}) => {
  return (
    <Suspense fallback={<LocationAutocompleteFormFieldSkeleton />}>
      <LocationAutoCompleteContent
        onPlaceSelected={onPlaceSelected}
        placeholder={placeholder}
        types={types}
        componentRestrictions={componentRestrictions}
        refValue={refValue}
        value={value}
        inputClassName={inputClassName}
        searchIconClassName={searchIconClassName}
        placeholderClassName={placeholderClassName}
        centerPlaceholder={centerPlaceholder}
      />
    </Suspense>
  )
}
